import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Copy, Trash2, ChevronDown, ChevronUp, Save } from "lucide-react";
import { Forbidden } from "./TeamSection";

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none";

export default function RolesSection() {
  const [roles, setRoles] = useState(null);
  const [meta, setMeta] = useState({ modules: [], actions: [] });
  const [openId, setOpenId] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const load = () =>
    api.get("/vendor/team/roles")
      .then((r) => setRoles(r.data.roles || []))
      .catch((e) => { if (e.response?.status === 403) setForbidden(true); setRoles([]); });

  useEffect(() => {
    load();
    api.get("/vendor/team/me/permissions").then((r) => setMeta({ modules: r.data.modules, actions: r.data.actions })).catch(() => {});
  }, []);

  const duplicate = async (r) => {
    try {
      const { data } = await api.post(`/vendor/team/roles/${r.id}/duplicate`);
      toast.success(`Rôle « ${data.role.name} » créé`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer le rôle « ${r.name} » ?`)) return;
    try {
      await api.delete(`/vendor/team/roles/${r.id}`);
      toast.success("Rôle supprimé");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
  };

  const createRole = async () => {
    try {
      const { data } = await api.post("/vendor/team/roles", { name: "Nouveau rôle", description: "", permissions: {} });
      toast.success("Rôle créé — configurez ses permissions");
      await load();
      setOpenId(data.role.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
  };

  if (forbidden) return <Forbidden />;
  if (roles === null) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>;

  return (
    <div className="space-y-3" data-testid="roles-section">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">{roles.length} rôle{roles.length > 1 ? "s" : ""} — 6 modèles prédéfinis, tous personnalisables et duplicables. Le propriétaire a toujours tous les droits.</div>
        <button onClick={createRole} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700" data-testid="roles-add-btn">
          <Plus className="h-3.5 w-3.5" /> Nouveau rôle
        </button>
      </div>
      {roles.map((r) => (
        <RoleCard key={r.id} role={r} meta={meta} open={openId === r.id}
          onToggle={() => setOpenId(openId === r.id ? null : r.id)}
          onDuplicate={() => duplicate(r)} onDelete={() => remove(r)} onSaved={load} />
      ))}
    </div>
  );
}

function RoleCard({ role, meta, open, onToggle, onDuplicate, onDelete, onSaved }) {
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description || "");
  const [perms, setPerms] = useState(role.permissions || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setName(role.name); setDescription(role.description || ""); setPerms(role.permissions || {}); }, [role]);

  const toggle = (m, a) => setPerms({ ...perms, [m]: { ...(perms[m] || {}), [a]: !(perms[m] || {})[a] } });
  const toggleRow = (m) => {
    const all = meta.actions.every((a) => (perms[m] || {})[a.key]);
    setPerms({ ...perms, [m]: Object.fromEntries(meta.actions.map((a) => [a.key, !all])) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/vendor/team/roles/${role.id}`, { name, description, permissions: perms });
      toast.success("Rôle enregistré");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`role-card-${role.name}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50/60 transition-colors" data-testid={`role-toggle-${role.name}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900 text-sm">{role.name}</span>
            {role.system_template && <span className="px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[9px] font-bold uppercase">Modèle</span>}
            <span className="text-[11px] text-slate-400">{role.members_count} membre{role.members_count > 1 ? "s" : ""}</span>
          </div>
          {role.description && <div className="text-xs text-slate-500 truncate">{role.description}</div>}
        </div>
        <span onClick={(e) => { e.stopPropagation(); onDuplicate(); }} className="h-7 w-7 rounded-md hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center cursor-pointer" title="Dupliquer" data-testid={`role-duplicate-${role.name}`}>
          <Copy className="h-3.5 w-3.5" />
        </span>
        <span onClick={(e) => { e.stopPropagation(); onDelete(); }} className="h-7 w-7 rounded-md hover:bg-red-50 text-red-500 inline-flex items-center justify-center cursor-pointer" title="Supprimer" data-testid={`role-delete-${role.name}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-4" data-testid={`role-editor-${role.name}`}>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Nom du rôle" data-testid="role-name-input" />
            <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} placeholder="Description" data-testid="role-desc-input" />
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500">Module</th>
                  {meta.actions.map((a) => <th key={a.key} className="px-2 py-2 font-semibold text-slate-500 text-center whitespace-nowrap">{a.label}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {meta.modules.map((m) => (
                  <tr key={m.key} className="hover:bg-violet-50/30">
                    <td className="px-3 py-1.5">
                      <button onClick={() => toggleRow(m.key)} className="font-medium text-slate-700 hover:text-violet-700" title="Tout cocher/décocher" data-testid={`role-row-${m.key}`}>{m.label}</button>
                    </td>
                    {meta.actions.map((a) => (
                      <td key={a.key} className="px-2 py-1.5 text-center">
                        <input type="checkbox" checked={!!(perms[m.key] || {})[a.key]} onChange={() => toggle(m.key, a.key)}
                          className="h-3.5 w-3.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
                          data-testid={`perm-${m.key}-${a.key}`} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 h-9 px-5 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50" data-testid="role-save-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Enregistrer le rôle
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
